import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from '@tanstack/react-router';
import { CircleArrowLeft, Loader2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { useCreateRecitalMutation } from '~entities/recital-manager/api/create-recital';
import type { CreateRecitalParams } from '~entities/recital-manager/api/create-recital';

import { Button } from '~/shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '~/shared/shadcn/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/shared/shadcn/ui/form';
import { Input } from '~/shared/shadcn/ui/input';
import { Textarea } from '~/shared/shadcn/ui/textarea';

const recitalFormSchema = z.object({
  name: z.string().min(1, 'Recital name is required'),
  year: z.coerce.number().min(2020, 'Year must be 2020 or later'),
  startDate: z.string().min(1, 'Start date is required'),
  endDate: z.string().min(1, 'End date is required'),
  description: z.string().optional(),
  allowParentSizing: z.boolean().default(true),
  allowVolunteerSignup: z.boolean().default(true),
  checkInEnabled: z.boolean().default(false),
  autoNotifyOnPublish: z.boolean().default(true),
});

type RecitalFormValues = z.infer<typeof recitalFormSchema>;

export const CreateRecitalPage = () => {
  const navigate = useNavigate();
  const { mutateAsync: createRecital, isPending: isCreatingRecital } =
    useCreateRecitalMutation();

  const form = useForm<RecitalFormValues>({
    resolver: zodResolver(recitalFormSchema),
    defaultValues: {
      name: '',
      year: new Date().getFullYear(),
      startDate: '',
      endDate: '',
      description: '',
      allowParentSizing: true,
      allowVolunteerSignup: true,
      checkInEnabled: false,
      autoNotifyOnPublish: true,
    },
  });

  const onSubmit = async (values: RecitalFormValues) => {
    const params: CreateRecitalParams = {
      name: values.name,
      year: values.year,
      startDate: values.startDate,
      endDate: values.endDate,
      description: values.description,
      settings: {
        allowParentSizing: values.allowParentSizing,
        allowVolunteerSignup: values.allowVolunteerSignup,
        checkInEnabled: values.checkInEnabled,
        autoNotifyOnPublish: values.autoNotifyOnPublish,
      },
    };

    try {
      const data = await createRecital(params);
      toast.success('Recital created successfully!');
      void navigate({
        to: '/recitals/$recitalId',
        params: { recitalId: data._id },
      });
    } catch (error) {
      console.error('Failed to create recital', error);
    }
  };

  return (
    <div className='mx-auto max-w-4xl'>
      <Link to='/recitals' className='mb-6 flex w-max items-center gap-2'>
        <CircleArrowLeft className='size-6' />
        <h2 className='text-2xl font-semibold'>Create New Recital</h2>
      </Link>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-6'>
          <Card>
            <CardHeader>
              <CardTitle>Recital Information</CardTitle>
              <CardDescription>
                Enter the basic details about your recital event
              </CardDescription>
            </CardHeader>
            <CardContent className='space-y-4'>
              <FormField
                control={form.control}
                name='name'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Recital Name *</FormLabel>
                    <FormControl>
                      <Input placeholder='2025 Spring Showcase' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='year'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Year *</FormLabel>
                    <FormControl>
                      <Input type='number' placeholder='2025' {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className='grid grid-cols-2 gap-4'>
                <FormField
                  control={form.control}
                  name='startDate'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Start Date *</FormLabel>
                      <FormControl>
                        <Input type='date' {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='endDate'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>End Date *</FormLabel>
                      <FormControl>
                        <Input type='date' {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name='description'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder='Annual spring recital featuring all our dance classes...'
                        className='resize-none'
                        rows={3}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* <Card>
            <CardHeader>
              <CardTitle>Settings</CardTitle>
              <CardDescription>
                Configure recital features and permissions
              </CardDescription>
            </CardHeader>
            <CardContent className='space-y-4'>
              <FormField
                control={form.control}
                name='allowParentSizing'
                render={({ field }) => (
                  <FormItem className='flex flex-row items-center justify-between rounded-lg border p-4'>
                    <div className='space-y-0.5'>
                      <FormLabel className='text-base'>
                        Allow Parent Costume Sizing
                      </FormLabel>
                      <FormDescription>
                        Let parents submit costume sizes for their students
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='allowVolunteerSignup'
                render={({ field }) => (
                  <FormItem className='flex flex-row items-center justify-between rounded-lg border p-4'>
                    <div className='space-y-0.5'>
                      <FormLabel className='text-base'>
                        Allow Volunteer Signup
                      </FormLabel>
                      <FormDescription>
                        Enable parents to sign up as volunteers
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='checkInEnabled'
                render={({ field }) => (
                  <FormItem className='flex flex-row items-center justify-between rounded-lg border p-4'>
                    <div className='space-y-0.5'>
                      <FormLabel className='text-base'>
                        Enable Check-In System
                      </FormLabel>
                      <FormDescription>
                        Allow QR code check-ins for students and volunteers
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='autoNotifyOnPublish'
                render={({ field }) => (
                  <FormItem className='flex flex-row items-center justify-between rounded-lg border p-4'>
                    <div className='space-y-0.5'>
                      <FormLabel className='text-base'>
                        Auto-Notify on Publish
                      </FormLabel>
                      <FormDescription>
                        Automatically send email notifications when recital is
                        published
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </CardContent>
          </Card> */}

          <div className='flex justify-end gap-4'>
            <Button
              type='button'
              variant='outline'
              onClick={() => navigate({ to: '/recitals' })}
            >
              Cancel
            </Button>
            <Button type='submit' disabled={isCreatingRecital}>
              {isCreatingRecital && <Loader2 className='animate-spin' />}
              Create Recital
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};
